import { type Signal, useSignal } from "@preact/signals";
import { useAutogrow } from "~/web/src/autogrow";
import type { Channel } from "~/web/src/channel";
import type { SheetMode } from "~/web/src/project/sheet";

// The composer at the foot of the run (mockup ux/06): the textarea is always
// typeable, submit is a no-op unless it is the user's turn (status !== running,
// spec chat protocol); the user's own line appears on the server echo. Its
// buttons are the only way the right-hand sheet opens.

function Icon({ d }: { d: string }) {
  return (
    <svg aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
      <path d={d} />
    </svg>
  );
}

const SCRIPT = "M4 20h4l10-10-4-4L4 16v4zM13 7l4 4";
const AUDIO = "M4 10v4M8 6v12M12 9v6M16 4v16M20 10v4";
const FILES = "M4 6a2 2 0 012-2h4l2 2h6a2 2 0 012 2v10a2 2 0 01-2 2H6a2 2 0 01-2-2z";

export function Composer({
  channel,
  sheet,
}: {
  channel: Channel;
  sheet: Signal<SheetMode | null>;
}) {
  const draft = useSignal("");
  const box = useAutogrow(draft.value);
  const agent = channel.agent.value;
  const running = agent.status === "running";
  const myTurn = !running && channel.connected.value;
  const asked = agent.status === "awaiting_user" && agent.ask?.kind === "question";

  function submit() {
    const text = draft.value.trim();
    if (!myTurn || !text) return;
    draft.value = "";
    void channel.send({ kind: "chat", text }).then((r) => {
      if (!r.ok) draft.value = text; // busy/disconnected: hand the text back
    });
  }

  const placeholder = asked
    ? "Reply to the explorer…"
    : channel.chat.value.length === 0
      ? "What should the video show?"
      : running
        ? "You can add notes once the video is ready."
        : "Say what to change, e.g. “Hold on the pricing table longer”";

  return (
    <form
      class={`composer bottom ${running ? "locked" : ""}`}
      onSubmit={(e) => {
        e.preventDefault();
        submit();
      }}
    >
      <textarea
        ref={box}
        rows={1}
        aria-label="Message"
        placeholder={placeholder}
        value={draft.value}
        onInput={(e) => {
          draft.value = e.currentTarget.value;
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            submit();
          }
        }}
      />
      <div class="row">
        <span class="secondary">
          <button
            class="btn ghost"
            type="button"
            onClick={() => {
              sheet.value = "script";
            }}
          >
            <Icon d={SCRIPT} />
            <span class="lbl">Edit script</span>
          </button>
          <button
            class="btn ghost"
            type="button"
            onClick={() => {
              sheet.value = "audio";
            }}
          >
            <Icon d={AUDIO} />
            <span class="lbl">Edit audio</span>
          </button>
          <button
            class="btn ghost"
            type="button"
            onClick={() => {
              sheet.value = "files";
            }}
          >
            <Icon d={FILES} />
            <span class="lbl">Files</span>
          </button>
        </span>
        <span class="spacer" />
        <button class="btn" type="submit" disabled={!myTurn}>
          {running && <span class="spin" />}
          {running ? "Creating…" : "Send"}
        </button>
      </div>
    </form>
  );
}
